import React, { useEffect, useMemo, useReducer, useState } from 'react';
import { Tabs, Tab, IconButton, Button } from '@material-ui/core';
import CancelIcon from '@material-ui/icons/Cancel';
import { useDispatch, useSelector } from 'react-redux';
import { decryptLocalStorage } from '../../../../../utils/authUtils';
import { formDataValidate, danger, success } from '../../../../../utils/formUtils';
import { notificationManager } from '../../../../../utils/NotificationUtils';
import {
  createEmailServiceTemplate,
  updateEmailServiceWithHtml,
} from '../../../../../utils/cmsUtils';
import { getEmailTemplateVariables } from '../../../../../utils/ServicesAPIUtils';
import { fetchAllEmailServices } from '../../../../services/servicesSlice';
import {
  allRoomsSelector,
  allRoomTypesSelector,
  fetchAllAccommodations,
} from '../../../../property/propertySlice';
import LinearProgressBar from '../../../../../shared/progress-bar/Progress';
import EmailServiceDetailsTab from './EmailServiceDetailsTab';
import EmailServiceTemplateTab from './EmailServiceTemplateTab';
import EmailServiceAudienceTab from './EmailServiceAudienceTab';
import EmailServiceTriggerTab from './EmailServiceTriggerTab';
import '../guest-create/EmailCreate.scss';
import undoIcon from '../../../../../assets/img/Undo.svg';
import redoIcon from '../../../../../assets/img/Redo.svg';

const drawerWidths = {
  default: '1000px',
  template: 'min(100vw, 1280px)',
};

const deriveAudienceInitialState = (obj) => {
  const base = {
    recipientType: 'Guests',
    audienceType: [],
    residentRoom: [],
    roomType: [],
    blockCodes: [],
    loyaltyTiers: [],
    audienceRelations: {},
    staffEmails: '',
    staffEmailList: [],
  };

  if (!obj || !obj.audience) return base;

  const { audience } = obj;

  const staffEmails = Array.isArray(audience.staff?.emails) ? audience.staff.emails : [];

  const isStaffAudience = audience.type === 'STAFF' || (!audience.type && staffEmails.length > 0);

  if (isStaffAudience) {
    return {
      ...base,
      recipientType: 'Staff',
      audienceType: [],
      residentRoom: [],
      roomType: [],
      blockCodes: [],
      loyaltyTiers: [],
      audienceRelations: {},
      staffEmails: staffEmails.join(', '),
      staffEmailList: staffEmails,
    };
  }

  const guests = audience.guests || {};
  const types = [];
  const relations = {};

  if (Array.isArray(guests.rooms?.values) && guests.rooms.values.length) {
    types.push('Rooms');
  }
  if (Array.isArray(guests.roomTypes?.values) && guests.roomTypes.values.length) {
    types.push('Room Type');
  }
  if (Array.isArray(guests.blockCodes?.values) && guests.blockCodes.values.length) {
    types.push('Block Code');
  }
  if (Array.isArray(guests.loyaltyTiers?.values) && guests.loyaltyTiers.values.length) {
    types.push('Loyalty Tier');
  }

  if (guests.rooms?.logic) relations.Rooms = guests.rooms.logic;
  if (guests.roomTypes?.logic) relations.RoomType = guests.roomTypes.logic;
  if (guests.blockCodes?.logic) relations.BlockCode = guests.blockCodes.logic;
  if (guests.loyaltyTiers?.logic) relations.LoyaltyTier = guests.loyaltyTiers.logic;

  return {
    ...base,
    recipientType: 'Guests',
    audienceType: types,
    residentRoom: guests.rooms?.values || [],
    roomType: guests.roomTypes?.values || [],
    blockCodes: guests.blockCodes?.values || [],
    loyaltyTiers: guests.loyaltyTiers?.values || [],
    audienceRelations: relations,
  };
};

const EmailServiceCreateView = ({
  onClose,
  obj,
  hotelId,
  defaultFromAddress,
  showLoader = () => {},
  onTabChange,
  originalRoomList = [],
  originalRoomTypeList = [],
  originalFloorList = [],
  mode: initialMode,
}) => {
  const dispatch = useDispatch();
  const selectedLanguage = decryptLocalStorage('lang');
  const language = selectedLanguage !== 'en' ? selectedLanguage : '';

  const [mode, setMode] = useState(() => {
    if (initialMode === 'view') return 'view';
    return obj ? 'edit' : 'create';
  });
  const isViewOnly = mode === 'view';

  const [tabValue, setTabValue] = useState(0);
  const [drawerWidth, setDrawerWidth] = useState(drawerWidths.default);
  const [saving, setSaving] = useState(false);
  const [templateStage, setTemplateStage] = useState('pick');
  const [isTemplateValid, setIsTemplateValid] = useState(mode === 'edit');
  const templateTabRef = React.useRef(null);
  const [templateTypes, setTemplateTypes] = useState([]);
  const [loadingTemplateTypes, setLoadingTemplateTypes] = useState(false);

  const originalRoomListFromStore = useSelector(allRoomsSelector);
  const originalRoomTypeListFromStore = useSelector(allRoomTypesSelector);
  const roomList = originalRoomList.length > 0 ? originalRoomList : originalRoomListFromStore;
  const roomTypeList =
    originalRoomTypeList.length > 0 ? originalRoomTypeList : originalRoomTypeListFromStore;

  const registerTemplateHistoryApi = (api) => {
    templateTabRef.current = api;
  };
  const handleUndo = () => templateTabRef.current?.undo?.();
  const handleRedo = () => templateTabRef.current?.redo?.();
  const audienceInitial = useMemo(() => deriveAudienceInitialState(obj), [obj]);

  const initialFormState = useMemo(
    () => ({
      name: {
        value: obj?.name || '',
        fieldName: 'Email Template Name',
        validations: ['required'],
        errors: {},
        tab: 0,
      },
      fromName: {
        value: obj?.fromName || '',
        fieldName: 'From Name',
        validations: ['required'],
        errors: {},
        tab: 0,
      },
      fromAddress: {
        value: obj?.fromAddress || '',
        fieldName: 'From Address',
        validations: ['required', 'validEmail'],
        errors: {},
        tab: 0,
      },
      cc: {
        value: obj?.cc || '',
        fieldName: 'CC',
        validations: ['validEmail'],
        errors: {},
        tab: 0,
      },
      bcc: {
        value: obj?.bcc || '',
        fieldName: 'BCC',
        validations: ['validEmail'],
        errors: {},
        tab: 0,
      },
      subject: {
        value: obj?.subject || '',
        fieldName: 'Subject Line',
        validations: ['required'],
        errors: {},
        tab: 0,
      },
      templateType: {
        value: obj?.templateType || '',
        fieldName: 'Template Type',
        validations: ['required'],
        errors: {},
        tab: 0,
      },

      templateMode: {
        value: obj?.htmlTemplateBody ? 'Custom' : '',
        fieldName: 'Template Mode',
        validations: [],
        errors: {},
        tab: 1,
      },
      
      htmlTemplateBody: {
        value: obj?.htmlTemplateBody || '',
        fieldName: 'HTML Template Body',
        validations: [],
        errors: {},
        tab: 1,
      },

      recipientType: {
        value: audienceInitial.recipientType,
        fieldName: 'Recipient',
        validations: [],
        errors: {},
        tab: 2,
      },
      audienceType: {
        value: audienceInitial.audienceType,
        fieldName: 'Select Audience Type(s)',
        validations: [],
        errors: {},
        tab: 2,
      },
      residentRoom: {
        value: audienceInitial.residentRoom,
        fieldName: 'Rooms',
        validations: [],
        errors: {},
        tab: 2,
      },
      roomType: {
        value: audienceInitial.roomType,
        fieldName: 'Room Type',
        validations: [],
        errors: {},
        tab: 2,
      },
      blockCodes: {
        value: audienceInitial.blockCodes,
        fieldName: 'Block Code',
        validations: ['required'],
        errors: {},
        tab: 2,
      },
      loyaltyTiers: {
        value: audienceInitial.loyaltyTiers,
        fieldName: 'Loyalty Tier',
        validations: ['required'],
        errors: {},
        tab: 2,
      },
      audienceRelations: {
        value: audienceInitial.audienceRelations,
        fieldName: 'Relations',
        validations: [],
        errors: {},
        tab: 2,
      },
      staffEmails: {
        value: audienceInitial.staffEmails,
        fieldName: 'Staff Emails',
        validations: [],
        errors: {},
        tab: 2,
      },
      staffEmailList: {
        value: audienceInitial.staffEmailList,
        fieldName: 'Staff Email List',
        validations: [],
        errors: {},
        tab: 2,
      },
    }),
    [
      audienceInitial,
      obj?.bcc,
      obj?.cc,
      obj?.fromAddress,
      obj?.fromName,
      obj?.htmlTemplateBody,
      obj?.name,
      obj?.subject,
      obj?.templateType,
    ],
  );

  const [formData, setFormData] = useReducer(
    (state, updates) => ({ ...state, ...updates }),
    initialFormState,
  );
  const formInitialStateRef = React.useRef(initialFormState);

  useEffect(() => {
    formInitialStateRef.current = initialFormState;
    setFormData(initialFormState);
  }, [initialFormState]);

  useEffect(() => {
    const fetchTemplateTypes = async () => {
      try {
        setLoadingTemplateTypes(true);
        const response = await getEmailTemplateVariables('templateTypes');
        const types = response?.data?.listEmailTemplateVariables || [];
        setTemplateTypes(types);
      } catch (error) {
        console.error('Error fetching template types:', error);
      } finally {
        setLoadingTemplateTypes(false);
      }
    };

    fetchTemplateTypes();
  }, []);

  const updateDrawerWidth = (index) => {
    const newWidth = index === 1 ? drawerWidths.template : drawerWidths.default;
    setDrawerWidth(newWidth);
    onTabChange && onTabChange({ tabIndex: index, width: newWidth });
  };
  useEffect(() => {
    updateDrawerWidth(tabValue);
  }, [tabValue]);

  useEffect(() => {
    if (tabValue === 2 && formData?.recipientType?.value === 'Guests' && hotelId) {
      dispatch(fetchAllAccommodations(hotelId));
    }
  }, [tabValue, formData?.recipientType?.value, hotelId, dispatch]);

  const handleInputChange = (patch) => {
    if (isViewOnly) return;
    setFormData(patch);
  };

  const validateTab = (tabIndex) => {
    const slice = Object.fromEntries(
      Object.entries(formData).filter(([_, meta]) => meta.tab === tabIndex),
    );
    const updated = {};
    const ok = formDataValidate(slice, updated);
    if (!ok) setFormData(updated);
    return ok;
  };

  const saveNow = async () => {
    const lang = decryptLocalStorage('lang');
    const selectedLang = lang !== 'en' ? lang : '';
    const recipientType = formData?.recipientType?.value || 'Guests';
    const audienceType = recipientType === 'Guests' ? 'GUESTS' : 'STAFF';
    const audience = { type: audienceType };

    if (recipientType === 'Guests') {
      audience.guests = {
        rooms: {
          logic: formData.audienceRelations?.value?.Rooms || 'AND',
          values: formData.residentRoom?.value || [],
        },
        roomTypes: {
          logic: formData.audienceRelations?.value?.RoomType || 'AND',
          values: formData.roomType?.value || [],
        },
        blockCodes: {
          logic: formData.audienceRelations?.value?.BlockCode || 'AND',
          values: formData.blockCodes?.value || [],
        },
        loyaltyTiers: {
          logic: formData.audienceRelations?.value?.LoyaltyTier || 'AND',
          values: formData.loyaltyTiers?.value || [],
        },
      };
    } else if (recipientType === 'Staff') {
      audience.staff = { emails: formData.staffEmailList?.value || [] };
    }

    const payload = {
      id: obj?.id,
      hotelId: obj?.hotelId || hotelId,
      name: formData.name.value,
      fromName: formData.fromName.value,
      fromAddress: formData.fromAddress.value,
      cc: formData.cc.value,
      bcc: formData.bcc.value,
      subject: formData.subject.value,
      templateType: formData.templateType.value,
      htmlTemplateBody: formData.htmlTemplateBody.value,
      isPublished: obj?.isPublished || false,
      audience,
    };
    const isNew = !obj?.id;

    try {
      setSaving(true);
      showLoader(true);

      if (isNew) {
        await createEmailServiceTemplate(payload, selectedLang);
        notificationManager(success, 'Email Service created successfully');
      } else {
        await updateEmailServiceWithHtml(payload, selectedLang);
        notificationManager(success, 'Email Service updated successfully');
      }

      await dispatch(fetchAllEmailServices(payload.hotelId));

      if (isNew) {
        onClose && onClose();
        return;
      }

      setMode('view');
      setFormData({
        name: { ...formData.name, value: payload.name },
        fromName: { ...formData.fromName, value: payload.fromName },
        fromAddress: { ...formData.fromAddress, value: payload.fromAddress },
        cc: { ...formData.cc, value: payload.cc },
        bcc: { ...formData.bcc, value: payload.bcc },
        subject: { ...formData.subject, value: payload.subject },
        templateType: { ...formData.templateType, value: payload.templateType },
        htmlTemplateBody: { ...formData.htmlTemplateBody, value: payload.htmlTemplateBody },
      });
    } catch (e) {
      notificationManager(
        danger,
        e?.graphQLErrors?.[0]?.message || 'Email Service operation failed',
      );
    } finally {
      setSaving(false);
      showLoader(false);
    }
  };

  const handleSaveOnDetails = async () => {
    if (!validateTab(0)) return;
    await saveNow();
  };

  const handleNext = async () => {
    if (isViewOnly) return;
    if (tabValue === 0 && !validateTab(0)) return;
    if (tabValue < 2) setTabValue((v) => v + 1);
  };

  const handleCancel = () => {
    if (mode === 'edit') {
      setMode('view');
      setFormData(formInitialStateRef.current);
      return;
    }
    onClose && onClose();
  };

  const isTemplateTab = tabValue === 1;
  const isCustom = formData?.templateMode?.value === 'Custom';
  const isEditing = !!obj;
  const showFooterActions = !isTemplateTab && !isViewOnly;
  const showHistoryControls =
    isTemplateTab && (isCustom || templateStage === 'edit') && !isViewOnly;

  const pageTitle = (() => {
    if (isViewOnly) return 'View Template';
    if (isTemplateTab) {
      const type = formData?.templateMode?.value;
      if (type === 'Template')
        return templateStage === 'edit' ? 'Edit Template' : 'Choose Template';
      if (type === 'Custom') return isEditing ? 'Edit Template' : 'Create Template';
      return 'Template';
    }
    return isEditing ? 'Edit Template' : 'Add Template';
  })();

  const hasErrorsInTab = (tabIndex) => {
    return Object.values(formData).some((field) => {
      if (field.tab !== tabIndex) return false;
      return Object.values(field.errors || {}).some((errorMsg) => !!errorMsg);
    });
  };

  const isDetailsTabValid = () => {
    if (hasErrorsInTab(0)) return false;
    const requiredFields = ['name', 'fromName', 'fromAddress', 'subject', 'templateType'];
    return requiredFields.every((key) => {
      const field = formData[key];
      if (!field) return true;

      const isRequired = field.validations?.includes('required');
      if (!isRequired) return true;
      const value = typeof field.value === 'string' ? field.value.trim() : field.value;
      return !!value;
    });
  };

  const audienceSelected = (label) => (formData?.audienceType?.value || []).includes(label);

  const arrLen = (key) => (Array.isArray(formData?.[key]?.value) ? formData[key].value.length : 0);

  const isAudienceTabValid = () => {
    const rec = formData?.recipientType?.value || 'Guests';
    if (rec === 'Staff') {
      const listLen = (formData?.staffEmailList?.value || []).length;
      const emailErrors = formData?.staffEmails?.errors || {};
      const hasInvalid = !!emailErrors.invalid || !!emailErrors.duplicate;

      return listLen > 0 && !hasInvalid;
    }
    const types = formData?.audienceType?.value || [];
    if (!types.length) return false;

    if (audienceSelected('Rooms') && !arrLen('residentRoom')) return false;
    if (audienceSelected('Room Type') && !arrLen('roomType')) return false;
    if (audienceSelected('Block Code') && !arrLen('blockCodes')) return false;
    if (audienceSelected('Loyalty Tier') && !arrLen('loyaltyTiers')) return false;

    return true;
  };
  const canNext = (() => {
    if (tabValue === 0) return isDetailsTabValid();
    if (tabValue === 1) return isTemplateValid;
    if (tabValue === 2) return isAudienceTabValid();
    return true;
  })();

  const handleTabChange = (event, newValue) => {
    if (isViewOnly) {
      setTabValue(newValue);
      return;
    }

    if (newValue === tabValue) return;
    if (newValue === 2 && !isTemplateValid) {
      notificationManager(danger, 'Please provide a valid template content before proceeding.');
      return;
    }
    if (mode === 'edit') {
      return;
    }
    if (newValue < tabValue) {
      setTabValue(newValue);
      return;
    }

    if (tabValue === 0 && !validateTab(0)) {
      return;
    }
    if (tabValue === 2 && !isAudienceTabValid()) {
      return;
    }

    setTabValue(newValue);
  };

  return (
    <div className='category-create' style={{ width: drawerWidth }}>
      <div
        className={
          tabValue === 1
            ? 'd-flex align-items-center flex-column service-container'
            : 'container d-flex align-items-center flex-column service-container'
        }
        style={{ padding: '0 !important' }}
      >
        <div className='row px-3 w-100 mt-4 align-items-center'>
          <div className='col-auto d-flex align-items-center'>
            <span className='page-title me-3'>{pageTitle}</span>

            {showHistoryControls && (
              <>
                <div className='separator-line'></div>
                <div className='title-actions d-flex align-items-center'>
                  <IconButton
                    aria-label='undo'
                    size='small'
                    className='title-action-btn me-2'
                    onClick={handleUndo}
                    disabled={
                      !templateTabRef.current ||
                      (templateTabRef.current.canUndo && !templateTabRef.current.canUndo())
                    }
                  >
                    <img src={undoIcon} alt='Undo' />
                  </IconButton>
                  <IconButton
                    aria-label='redo'
                    size='small'
                    className='title-action-btn'
                    onClick={handleRedo}
                    disabled={
                      !templateTabRef.current ||
                      (templateTabRef.current.canRedo && !templateTabRef.current.canRedo())
                    }
                  >
                    <img src={redoIcon} alt='Redo' />
                  </IconButton>
                </div>
              </>
            )}
          </div>

          <div className='col d-flex align-items-center justify-content-end gap-3'>
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              indicatorColor='primary'
              textColor='primary'
              className='property-tabs'
              aria-label='email-service-tabs'
            >
              <Tab label='Details' />
              <Tab label='Template' />
              <Tab label='Audience' />
            </Tabs>
            <IconButton aria-label='cancel' onClick={onClose} className='ms-2'>
              <CancelIcon />
            </IconButton>
          </div>
        </div>
        <div className='row px-3 w-100 d-flex'>
          {tabValue === 0 && (
            <EmailServiceDetailsTab
              formData={formData}
              handleInputChange={handleInputChange}
              readOnly={isViewOnly}
              templateTypes={templateTypes}
              loadingTemplateTypes={loadingTemplateTypes}
            />
          )}
          {tabValue === 1 && (
            <EmailServiceTemplateTab
              formData={formData}
              handleInputChange={handleInputChange}
              onNext={() => {
                if (mode === 'edit') return;
                setTabValue(2);
              }}
              onSave={saveNow}
              onStageChange={setTemplateStage}
              onValidationChange={setIsTemplateValid}
              registerHistoryApi={registerTemplateHistoryApi}
              isViewOnly={isViewOnly}
              isEditing={isEditing}
            />
          )}
          {tabValue === 2 && (
            <EmailServiceAudienceTab
              formData={formData}
              handleInputChange={handleInputChange}
              setFormData={setFormData}
              originalRoomList={roomList}
              originalRoomTypeList={roomTypeList}
              hotelId={hotelId}
              language={language}
              isReadOnly={isViewOnly}
            />
          )}
        </div>

        {showFooterActions && tabValue !== 1 && (
          <div className='row mx-2 mt-auto px-4 w-100 pb-5'>
            <div className='col-md-12 d-flex justify-content-start'>
              {tabValue === 2 || (tabValue === 0 && isEditing) ? (
                <Button
                  disabled={
                    saving ||
                    (tabValue === 0 && isEditing && !isDetailsTabValid()) ||
                    (tabValue === 2 && !isAudienceTabValid())
                  }
                  onClick={tabValue === 0 ? handleSaveOnDetails : saveNow}
                  variant='contained'
                  color='primary'
                  className='me-3'
                  size='large'
                >
                  {tabValue === 2 && isEditing ? 'Save' : tabValue === 2 ? 'Add' : 'Save'}
                </Button>
              ) : (
                <Button
                  disabled={saving || !canNext}
                  onClick={handleNext}
                  variant='contained'
                  color='primary'
                  className='me-3'
                  size='large'
                >
                  Next
                </Button>
              )}

              <Button
                disabled={saving}
                variant='outlined'
                type='button'
                color='secondary'
                size='large'
                onClick={handleCancel}
              >
                Cancel
              </Button>
            </div>
          </div>
        )}

        {isViewOnly && (
          <div className='row mx-2 mt-auto px-4 w-100 pb-5'>
            <div className='col-md-12 d-flex justify-content-start'>
              <Button
                variant='contained'
                color='primary'
                className='me-3'
                size='large'
                onClick={() => {
                  setMode(obj ? 'edit' : 'create');
                }}
              >
                Edit
              </Button>
            </div>
          </div>
        )}

        {saving && (
          <div className='pt-4 col-md-12'>
            <LinearProgressBar />
          </div>
        )}
      </div>
    </div>
  );
};

export default EmailServiceCreateView;
